import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { FormInstance } from "antd";
import { HistoryOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdRevenueAdType,
  AdRevenueConfigRow,
  AdRevenueConfigScope,
  AdRevenueRuntimeStatus,
  AdRevenueUnconfiguredCandidate,
} from "../../shared/types/AdRevenueManagement";
import { disableAdRevenueConfig, getAdRevenueConfigs, saveAdRevenueConfig } from "./adRevenueAdminApi";
import { FilterBar, Section, SectionHeader } from "./commercialAnalyticsStyles";
import { formatDateTime, formatInteger } from "./commercialAnalyticsUtils";

interface ConfigFormValues {
  scope: AdRevenueConfigScope;
  adType: AdRevenueAdType;
  adUnitId?: string;
  adPlacementName?: string;
  revenueYuan: number;
  effectiveMode: "now" | "scheduled";
  effectiveFrom?: Dayjs;
  applyToWaiting: boolean;
}

const AD_TYPE_OPTIONS = [
  { label: "激励视频", value: "rewardedVideo" },
  { label: "插屏广告", value: "interstitial" },
];

const SCOPE_OPTIONS = [
  { label: "广告位精确配置", value: "ad_unit" },
  { label: "广告类型兜底", value: "ad_type" },
];

export default function AdRevenueConfigView() {
  const [form] = Form.useForm<ConfigFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<AdRevenueConfigScope>();
  const [adTypeFilter, setAdTypeFilter] = useState<AdRevenueAdType>();
  const [configs, setConfigs] = useState<AdRevenueConfigRow[]>([]);
  const [history, setHistory] = useState<AdRevenueConfigRow[]>([]);
  const [candidates, setCandidates] = useState<AdRevenueUnconfiguredCandidate[]>([]);
  const [runtime, setRuntime] = useState<AdRevenueRuntimeStatus>();
  const [editing, setEditing] = useState<AdRevenueConfigRow>();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalScope, setModalScope] = useState<AdRevenueConfigScope>("ad_unit");
  const [effectiveMode, setEffectiveMode] = useState<"now" | "scheduled">("now");
  const [historyTarget, setHistoryTarget] = useState<AdRevenueConfigRow>();
  const loadSequence = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const data = await getAdRevenueConfigs({
        scope: scopeFilter,
        adType: adTypeFilter,
        includeHistory: true,
        historyLimit: 200,
        includeCandidates: true,
      });
      if (sequence !== loadSequence.current) return;
      setConfigs(data.current);
      setHistory(data.history);
      setCandidates(data.candidates);
      setRuntime(data.runtime);
    } catch (error) {
      if (sequence === loadSequence.current) {
        message.error(readErrorMessage(error, "收益配置加载失败"));
      }
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [adTypeFilter, scopeFilter]);

  useEffect(() => {
    void load();
    return () => { loadSequence.current += 1; };
  }, [load]);

  const openCreate = (candidate?: AdRevenueUnconfiguredCandidate) => {
    const scope: AdRevenueConfigScope = candidate?.adUnitId ? "ad_unit" : "ad_type";
    setEditing(undefined);
    setModalScope(scope);
    setEffectiveMode("now");
    form.setFieldsValue({
      scope,
      adType: candidate?.adType || "rewardedVideo",
      adUnitId: candidate?.adUnitId,
      adPlacementName: candidate?.adPlacementName,
      revenueYuan: undefined as unknown as number,
      effectiveMode: "now",
      effectiveFrom: undefined,
      applyToWaiting: true,
    });
    setModalOpen(true);
  };

  const openEdit = (row: AdRevenueConfigRow) => {
    const isScheduled = row.enabled && row.effectiveFrom > Date.now();
    setEditing(row);
    setModalScope(row.scope);
    setEffectiveMode(isScheduled ? "scheduled" : "now");
    form.setFieldsValue({
      scope: row.scope,
      adType: row.adType,
      adUnitId: row.adUnitId,
      adPlacementName: row.adPlacementName,
      revenueYuan: row.revenueCent / 100,
      effectiveMode: isScheduled ? "scheduled" : "now",
      effectiveFrom: isScheduled ? dayjs(row.effectiveFrom) : undefined,
      applyToWaiting: !isScheduled,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    let values: ConfigFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const revenueCent = Math.round(Number(values.revenueYuan) * 100);
    if (!Number.isInteger(revenueCent) || revenueCent < 1) {
      message.error("收益金额必须能够换算为至少 1 分的整数值");
      return;
    }
    setSaving(true);
    try {
      const data = await saveAdRevenueConfig({
        scope: values.scope,
        adType: values.adType,
        adUnitId: values.scope === "ad_unit" ? values.adUnitId?.trim() : undefined,
        adPlacementName: values.adPlacementName?.trim(),
        revenueCent,
        effectiveFrom: values.effectiveMode === "scheduled" ? values.effectiveFrom?.valueOf() : undefined,
        expectedRevision: editing?.logicalRevision || 0,
        replaceConfigId: editing?.enabled ? editing.id : undefined,
        applyToWaiting: values.effectiveMode === "now" && values.applyToWaiting,
      });
      if (data.status === "revision_conflict") {
        Modal.warning({
          title: "配置已被其他管理员更新",
          content: `当前版本为 ${data.current?.logicalRevision ?? data.current?.revision ?? "未知"}，你编辑的是版本 ${editing?.logicalRevision ?? 0}。请关闭窗口并重新加载后再修改。`,
        });
        return;
      }
      message.success(values.effectiveMode === "now" && values.applyToWaiting
        ? "收益配置已发布，待配置任务将由游戏服按事件时间分批补齐"
        : "收益配置已发布");
      setModalOpen(false);
      await load();
    } catch (error) {
      message.error(readErrorMessage(error, "收益配置保存失败"));
    } finally {
      setSaving(false);
    }
  };

  const disable = (row: AdRevenueConfigRow) => {
    const isScheduled = row.enabled && row.effectiveFrom > Date.now();
    Modal.confirm({
      title: isScheduled ? "确认取消定时配置？" : "确认停用收益配置？",
      content: isScheduled
        ? "取消后会恢复与前后价格区间的衔接，不会删除历史记录。"
        : "停用不会删除历史记录。之后未命中其他配置的广告播放会进入“等待配置”。",
      okText: isScheduled ? "取消定时" : "停用",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        try {
          const data = await disableAdRevenueConfig({ configId: row.id, expectedRevision: row.logicalRevision });
          if (data.status === "revision_conflict") {
            Modal.warning({
              title: "配置已被其他管理员更新",
              content: `最新版本为 ${data.current?.logicalRevision ?? data.current?.revision ?? "未知"}，请刷新页面后重试。`,
            });
            return;
          }
          if (data.status === "not_found") {
            message.warning("配置不存在或已被删除，请刷新页面");
            await load();
            return;
          }
          message.success(isScheduled ? "定时配置已取消" : "配置已停用");
          await load();
        } catch (error) {
          message.error(readErrorMessage(error, "收益配置停用失败"));
        }
      },
    });
  };

  const historyRows = useMemo(() => {
    if (!historyTarget) return [];
    return history.filter((row) => sameLogicalConfig(row, historyTarget));
  }, [history, historyTarget]);

  return (
    <>
      <Alert
        showIcon
        type="info"
        message="配置优先级：广告位精确配置 > 广告类型兜底 > 等待配置"
        description="后台按“元”输入，发布时换算为整数“分”。已生成或已上报任务保留原配置快照，不会因后续调价而改变。"
      />

      <RuntimeSummary runtime={runtime} loading={loading} />

      <FilterBar>
        <Select
          allowClear
          placeholder="配置范围"
          value={scopeFilter}
          style={{ width: 160 }}
          options={SCOPE_OPTIONS}
          onChange={setScopeFilter}
        />
        <Select
          allowClear
          placeholder="广告类型"
          value={adTypeFilter}
          style={{ width: 130 }}
          options={AD_TYPE_OPTIONS}
          onChange={setAdTypeFilter}
        />
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>新增配置</Button>
      </FilterBar>

      <Section>
        <SectionHeader>
          <span>当前生效配置</span>
          <Typography.Text type="secondary">共 {formatInteger(configs.length)} 条</Typography.Text>
        </SectionHeader>
        <Table<AdRevenueConfigRow>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={configs}
          scroll={{ x: 1180 }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          columns={[
            { title: "范围", dataIndex: "scope", width: 135, render: renderScope },
            { title: "广告类型", dataIndex: "adType", width: 110, render: renderAdType },
            { title: "广告位名称", dataIndex: "adPlacementName", width: 150, ellipsis: true, render: emptyText },
            { title: "广告位 ID", dataIndex: "adUnitId", width: 180, ellipsis: true, render: emptyText },
            { title: "单次收益", dataIndex: "revenueCent", width: 125, render: renderRevenue },
            { title: "生效时间", dataIndex: "effectiveFrom", width: 170, render: formatDateTime },
            { title: "版本", dataIndex: "revision", width: 78, render: (value) => `v${value}` },
            { title: "修改人", width: 120, ellipsis: true, render: (_, row) => row.updatedBy || row.createdBy || "-" },
            { title: "状态", dataIndex: "enabled", width: 82, render: (enabled) => <Tag color={enabled ? "success" : "default"}>{enabled ? "启用" : "停用"}</Tag> },
            {
              title: "操作",
              fixed: "right",
              width: 190,
              render: (_, row) => (
                <Space size={2}>
                  <Button type="link" size="small" onClick={() => openEdit(row)}>修改</Button>
                  <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => setHistoryTarget(row)}>历史</Button>
                  <Button type="link" size="small" danger disabled={!row.enabled} onClick={() => disable(row)}>停用</Button>
                </Space>
              ),
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <span>历史与定时配置</span>
          <Typography.Text type="secondary">停用后可从这里发布新版本重新启用</Typography.Text>
        </SectionHeader>
        <Table<AdRevenueConfigRow>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={history}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 1180 }}
          columns={[
            { title: "范围", dataIndex: "scope", width: 135, render: renderScope },
            { title: "广告类型", dataIndex: "adType", width: 110, render: renderAdType },
            { title: "广告位名称", dataIndex: "adPlacementName", width: 150, ellipsis: true, render: emptyText },
            { title: "广告位 ID", dataIndex: "adUnitId", width: 180, ellipsis: true, render: emptyText },
            { title: "单次收益", dataIndex: "revenueCent", width: 120, render: renderRevenue },
            { title: "生效时间", dataIndex: "effectiveFrom", width: 170, render: formatDateTime },
            { title: "失效时间", dataIndex: "effectiveTo", width: 170, render: formatDateTime },
            { title: "版本", dataIndex: "revision", width: 72, render: (value) => `v${value}` },
            { title: "状态", width: 92, render: (_, row) => renderHistoricalState(row) },
            {
              title: "操作",
              fixed: "right",
              width: 210,
              render: (_, row) => canOperateHistoricalConfig(row)
                ? (
                  <Space size={0}>
                    <Button type="link" size="small" onClick={() => openEdit(row)}>
                      {row.enabled ? "修改定时配置" : "发布新版本"}
                    </Button>
                    {row.enabled && row.effectiveFrom > Date.now() && (
                      <Button type="link" size="small" danger onClick={() => disable(row)}>取消定时</Button>
                    )}
                  </Space>
                )
                : <Typography.Text type="secondary">历史版本（仅查看）</Typography.Text>,
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <span>待配置广告位</span>
          <Typography.Text type="secondary">已有成功播放、但未匹配收益配置的任务</Typography.Text>
        </SectionHeader>
        <Table<AdRevenueUnconfiguredCandidate>
          rowKey={(row) => `${row.adType}:${row.adUnitId || row.adPlacementName || "fallback"}`}
          size="small"
          loading={loading}
          dataSource={candidates}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 780 }}
          columns={[
            { title: "广告类型", dataIndex: "adType", width: 120, render: renderAdType },
            { title: "广告位名称", dataIndex: "adPlacementName", width: 170, ellipsis: true, render: emptyText },
            { title: "广告位 ID", dataIndex: "adUnitId", width: 210, ellipsis: true, render: emptyText },
            { title: "待配置任务", dataIndex: "waitingCount", width: 110, render: formatInteger },
            { title: "最早等待", dataIndex: "oldestWaitingAt", width: 170, render: formatDateTime },
            { title: "操作", width: 100, fixed: "right", render: (_, row) => <Button type="link" size="small" onClick={() => openCreate(row)}>立即配置</Button> },
          ]}
        />
      </Section>

      <Modal
        title={editing ? "修改收益配置" : "新增收益配置"}
        open={modalOpen}
        confirmLoading={saving}
        okText="发布配置"
        cancelText="取消"
        width={620}
        forceRender
        onCancel={() => setModalOpen(false)}
        onOk={() => void submit()}
      >
        <Form form={form} layout="vertical" preserve={false} initialValues={{ scope: "ad_unit", adType: "rewardedVideo", effectiveMode: "now", applyToWaiting: true }}>
          <Form.Item name="scope" label="配置范围" rules={[{ required: true }]}>
            <Radio.Group
              options={SCOPE_OPTIONS}
              disabled={!!editing}
              onChange={(event) => setModalScope(event.target.value as AdRevenueConfigScope)}
            />
          </Form.Item>
          <Form.Item name="adType" label="广告类型" rules={[{ required: true, message: "请选择广告类型" }]}>
            <Select options={AD_TYPE_OPTIONS} disabled={!!editing} />
          </Form.Item>
          {modalScope === "ad_unit" && (
            <Form.Item name="adUnitId" label="广告位 ID" rules={[{ required: true, whitespace: true, message: "请输入广告位 ID" }]}>
              <Input placeholder="例如 adunit-xxxxxxxx" disabled={!!editing} />
            </Form.Item>
          )}
          <Form.Item name="adPlacementName" label="广告位名称">
            <Input allowClear placeholder="用于后台展示与检索" />
          </Form.Item>
          <Form.Item
            name="revenueYuan"
            label="单次预估收益"
            rules={[
              { required: true, message: "请输入单次预估收益" },
              {
                validator: (_, value) => Number.isFinite(Number(value)) && Math.round(Number(value) * 100) >= 1
                  ? Promise.resolve()
                  : Promise.reject(new Error("请输入不少于 0.01 元的金额")),
              },
            ]}
            extra={<RevenuePreview form={form} />}
          >
            <InputNumber min={0.01} precision={2} step={0.01} addonAfter="元" style={{ width: "100%" }} placeholder="例如 0.15" />
          </Form.Item>
          <Form.Item name="effectiveMode" label="生效方式" rules={[{ required: true }]}>
            <Radio.Group
              options={[{ label: "立即生效", value: "now" }, { label: "指定时间", value: "scheduled" }]}
              onChange={(event) => {
                const mode = event.target.value as "now" | "scheduled";
                setEffectiveMode(mode);
                if (mode === "scheduled") {
                  form.setFieldValue("applyToWaiting", false);
                  if (editing?.enabled && editing.effectiveFrom > Date.now()) {
                    form.setFieldValue("effectiveFrom", dayjs(editing.effectiveFrom));
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, current) => prev.effectiveMode !== current.effectiveMode}>
            {({ getFieldValue }) => getFieldValue("effectiveMode") === "scheduled" ? (
              <Form.Item
                name="effectiveFrom"
                label="生效时间"
                preserve
                extra="指定时间只允许选择未来时刻；修改已发布的定时配置会安全替换原计划并修复前后价格区间。"
                rules={[
                  { required: true, message: "请选择生效时间" },
                  {
                    validator: (_, value) => !value || dayjs(value).isAfter(dayjs())
                      ? Promise.resolve()
                      : Promise.reject(new Error("指定生效时间必须晚于当前时间")),
                  },
                ]}
              >
                <DatePicker
                  showTime
                  style={{ width: "100%" }}
                  disabledDate={(date) => date.endOf("day").isBefore(dayjs())}
                />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item
            name="applyToWaiting"
            valuePropName="checked"
            extra={effectiveMode === "scheduled"
              ? "定时配置尚未生效，不会提前处理等待任务；生效后由游戏服按事件时间匹配。"
              : "游戏服会按事件时间和“广告位精确配置 > 广告类型兜底”的优先级分批补齐；不会修改已发送或已成功任务。"}
          >
            <Checkbox disabled={effectiveMode === "scheduled"}>
              为匹配且从未向腾讯发送的 waiting_config 任务补齐价格
            </Checkbox>
          </Form.Item>
          {editing && (
            <Alert type="warning" showIcon message={`当前编辑版本 v${editing.revision}；如保存期间被他人修改，系统会拒绝覆盖并提示重新加载。`} />
          )}
        </Form>
      </Modal>

      <Drawer
        title="收益配置历史"
        width="min(760px, 94vw)"
        open={!!historyTarget}
        destroyOnHidden
        onClose={() => setHistoryTarget(undefined)}
      >
        <Table<AdRevenueConfigRow>
          rowKey="id"
          size="small"
          dataSource={historyRows}
          pagination={false}
          scroll={{ x: 760 }}
          columns={[
            { title: "版本", dataIndex: "revision", width: 75, render: (value) => `v${value}` },
            { title: "收益", dataIndex: "revenueCent", width: 115, render: renderRevenue },
            { title: "生效时间", dataIndex: "effectiveFrom", width: 170, render: formatDateTime },
            { title: "失效时间", dataIndex: "effectiveTo", width: 170, render: formatDateTime },
            { title: "修改人", width: 120, render: (_, row) => row.updatedBy || row.createdBy || "-" },
            { title: "状态", width: 90, render: (_, row) => row.isCurrent ? <Tag color="success">当前</Tag> : <Tag>历史</Tag> },
          ]}
        />
      </Drawer>
    </>
  );
}

function RuntimeSummary({ runtime, loading }: { runtime?: AdRevenueRuntimeStatus; loading: boolean }) {
  const modeColor = runtime?.mode === "live" ? "success" : runtime?.mode === "capture" ? "processing" : "default";
  const workerState = getWorkerState(runtime);
  return (
    <Section aria-busy={loading}>
      <SectionHeader>
        <span>运行状态</span>
        <Space>
          <Tag color={modeColor}>模式：{runtime?.mode || "unknown"}</Tag>
          <Tag color={workerState.color}>Worker：{workerState.text}</Tag>
          <Tag>配置版本：{runtime?.configVersion ?? "-"}</Tag>
        </Space>
      </SectionHeader>
      <Descriptions size="small" column={{ xs: 2, sm: 4, md: 7 }}>
        <Descriptions.Item label="等待配置">{formatInteger(runtime?.queue.waitingConfig)}</Descriptions.Item>
        <Descriptions.Item label="待上报">{formatInteger(runtime?.queue.pending)}</Descriptions.Item>
        <Descriptions.Item label="仅采集">{formatInteger(runtime?.queue.captured)}</Descriptions.Item>
        <Descriptions.Item label="上报中">{formatInteger(runtime?.queue.reporting)}</Descriptions.Item>
        <Descriptions.Item label="失败">{formatInteger(runtime?.queue.failed)}</Descriptions.Item>
        <Descriptions.Item label="跳过">{formatInteger(runtime?.queue.skipped)}</Descriptions.Item>
        <Descriptions.Item label="成功">{formatInteger(runtime?.queue.succeeded)}</Descriptions.Item>
      </Descriptions>
      <Typography.Text type="secondary">
        最近心跳：{formatDateTime(runtime?.workerHeartbeatAt)}；最近配置加载：{formatDateTime(runtime?.lastConfigReloadAt)}
      </Typography.Text>
    </Section>
  );
}

function RevenuePreview({ form }: { form: FormInstance<ConfigFormValues> }) {
  const value = Form.useWatch("revenueYuan", form);
  const yuan = Number(value);
  if (!Number.isFinite(yuan)) return <span>数据库和腾讯请求均使用整数分。</span>;
  return <span>保存为 {Math.round(yuan * 100)} 分；腾讯请求 value = {Math.round(yuan * 100)}。</span>;
}

function sameLogicalConfig(left: AdRevenueConfigRow, right: AdRevenueConfigRow) {
  return left.scope === right.scope
    && left.adType === right.adType
    && (left.adUnitId || "") === (right.adUnitId || "");
}

function getWorkerState(runtime?: AdRevenueRuntimeStatus): { text: string; color?: string } {
  if (!runtime || runtime.mode === "unknown") return { text: "未确认", color: "warning" };
  if (runtime.mode === "off") return { text: "已停用" };
  if (runtime.mode === "capture") return { text: "仅采集，不发送", color: "processing" };
  if (runtime.workerEnabled === false) return { text: "未启用", color: "warning" };
  if (runtime.workerEnabled !== true) return { text: "启用状态未确认", color: "warning" };
  return runtime.workerHealthy
    ? { text: "正常", color: "success" }
    : { text: "心跳异常", color: "error" };
}

function renderScope(value: AdRevenueConfigScope) {
  return value === "ad_unit" ? <Tag color="blue">广告位精确</Tag> : <Tag color="purple">类型兜底</Tag>;
}

function renderAdType(value: string) {
  if (value === "rewardedVideo") return "激励视频";
  if (value === "interstitial") return "插屏广告";
  return value || "-";
}

function renderRevenue(value: unknown) {
  const cent = Number(value);
  return Number.isFinite(cent) ? (
    <Tooltip title={`${Math.round(cent)} 分`}><Typography.Text strong>¥{(cent / 100).toFixed(2)}</Typography.Text></Tooltip>
  ) : "-";
}

function renderHistoricalState(row: AdRevenueConfigRow) {
  if (row.enabled && row.effectiveFrom > Date.now()) return <Tag color="processing">待生效</Tag>;
  if (row.enabled) return <Tag color="warning">已失效</Tag>;
  return <Tag>已停用</Tag>;
}

function canOperateHistoricalConfig(row: AdRevenueConfigRow) {
  if (row.enabled && row.effectiveFrom > Date.now()) return true;
  return !row.enabled && row.revision === row.logicalRevision;
}

function emptyText(value: unknown) {
  return value == null || value === "" ? "-" : String(value);
}

function readErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}
